import React from 'react'
import { useTheme } from '@emotion/react'

import { ImageWidths } from 'common/helpers/image'
import { CommonCardProps } from 'component-library/cards/CardItem.Props'

import {
    StyledLandscapeLink,
    StyledMedia,
    StyledHeader,
    StyledHeadline,
    StyledSponsoredLabel,
    getLandscapeWrapperTag,
    StyledTeaser,
    StyledCardHeadlineSkeleton,
    StyledTimestamp,
    StyledInlineWrapper,
} from 'component-library/cards/Landscape/Landscape.styled'

import { toLinkWithHint } from 'common/routing/route-hints'
import { themedValue } from 'component-library/__styling/themed-value'
import { FontScales } from 'component-library/__styling/settings/fontScale'
import { getTimeRoundedMax } from 'common/helpers/time'
import { Product } from 'component-library/__product'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { CardByline } from '../CardByline/CardByline'
import { FixedRatio, CardBreakpointRatios } from 'common/client-data-types/image-item'
import { MediaMode } from '../CardMedia/CardMedia'
import {
    PublicationCardItemWithoutVideo,
    PublicationCardItem,
    isPublicationCardItemWithVideo,
} from 'common/client-data-types/card-types'
import { createCardClickedEvent } from 'common/events/collection-events'
import { TimestampType } from 'component-library/content/CardTimestamp/CardTimestamp'

export interface Props extends CommonCardProps {
    className?: string
    cardNumber: number

    asListElement?: boolean
    hideTeaser?: boolean
    hasBackground?: boolean
    hasPadding?: boolean
    hideByline?: boolean
    mediaMode?: MediaMode

    timestamp?: TimestampType

    // Images
    fixedRatio?: FixedRatio | FixedRatio[] | CardBreakpointRatios
    imageWidths?: ImageWidths['thewest'] | ImageWidths['perthnow'] | ImageWidths['sevennews']
    imagePosition?: 'left' | 'right'
    smallImage?: boolean
    item: MaybeLoaded<PublicationCardItem>
}

export const Landscape: React.FC<Props> = props => {
    const linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!props.item.loaded) {
            e.preventDefault()
            return
        }
        props.onEvent(
            createCardClickedEvent(
                props.item.result,
                'InternalLandscape',
                'Landscape',
                props.cardNumber
            )
        )
    }

    const theme = useTheme()
    const cardItem = props.item

    // Sponsored
    const showSponsoredStyles = cardItem.loaded && cardItem.result.isSponsored
    const isPerthNowSponsored = showSponsoredStyles && theme.kind === 'perthnow'

    // Text options
    const hideKicker = isPerthNowSponsored || props.hideKicker
    const showTeaser = props.hideTeaser === false
    const showByline = props.hideByline === false

    // Images
    const setImagePosition = props.imagePosition || 'left' // Default image position to left so it doesn't break existing cards
    const imageRatio = props.fixedRatio || '4:3'
    const imageRight = props.imagePosition === 'right'
    const imageLeft = props.imagePosition !== 'right'

    const StyledLandscape = getLandscapeWrapperTag(props.asListElement ? 'li' : 'div')

    const fontScale = themedValue(theme, {
        thewest: 0.75,
        perthnow: 0.75,
        sevennews: 0.83,
        fallback: 1,
    }) as FontScales

    const getKickerText = (cardItem: PublicationCardItemWithoutVideo) => {
        let time: string | undefined

        if (props.timestamp === 'rounded-in-kicker' && cardItem.publicationDate) {
            time = getTimeRoundedMax(cardItem.publicationDate)
        }

        return time ? time : cardItem.kicker
    }

    const getImageWidth = (product: Product) => {
        switch (product) {
            case Product.PerthNow:
                return 150 // was 140 rounded up to 150
            case Product.TheWest:
                return 150
            case Product.SevenNews:
                return 126
        }
    }

    const renderImage = (
        <StyledMedia
            item={cardItem}
            imageWidths={getImageWidth(theme.kind)}
            fixedRatio={imageRatio}
            onEvent={props.onEvent}
            disableImageLazyLoad={props.disableImageLazyLoad}
            imagePosition={setImagePosition}
            smallImage={props.smallImage}
            mediaMode={props.mediaMode}
        />
    )

    const renderHeadline = cardItem.loaded ? (
        <StyledHeadline
            kickerText={getKickerText(cardItem.result)}
            headlineText={cardItem.result.shortHeadline}
            isLive={cardItem.result.isLive}
            showVideoIcon={
                props.timestamp ? false : isPublicationCardItemWithVideo(cardItem.result)
            }
            isSponsored={cardItem.result.isSponsored}
            publicationKind={cardItem.result.publicationKind}
            isPremium={cardItem.result.isPremium}
            hideKicker={hideKicker}
            fontScale={fontScale}
            kickerVerticalSpacing={'sm'}
        />
    ) : (
        <StyledCardHeadlineSkeleton fontScale={fontScale} count={3} />
    )

    return (
        <StyledLandscape
            className={props.className}
            data-topic={cardItem.loaded && cardItem.result.primaryTopic.id}
            showSponsoredStyles={showSponsoredStyles}
            hasBackground={props.hasBackground || false}
            hasPadding={props.hasPadding}
        >
            <StyledLandscapeLink
                to={toLinkWithHint(cardItem)}
                onClick={linkClicked}
                isLoading={!cardItem.loaded}
                hasBackground={false}
            >
                {/* If not hiding, and we want image on the left! */}
                {props.mediaMode === 'hidden' ? null : imageLeft && renderImage}

                <StyledHeader hideKicker={props.hideKicker}>
                    {renderHeadline}
                    {cardItem.loaded && showTeaser && (
                        <StyledTeaser
                            teaserOrSkeletonLines={cardItem.loaded ? cardItem.result.teaser : 1}
                        />
                    )}
                    {cardItem.loaded && (
                        <StyledInlineWrapper>
                            {showByline && <CardByline item={cardItem} onEvent={props.onEvent} />}

                            {props.timestamp === 'full-at-bottom' && (
                                <StyledTimestamp
                                    time={cardItem.result.publicationDate}
                                    timestampStyle="full"
                                />
                            )}
                        </StyledInlineWrapper>
                    )}
                    {showSponsoredStyles && cardItem.loaded && (
                        <StyledSponsoredLabel source={cardItem.result.byline} />
                    )}
                </StyledHeader>

                {/* If not hiding  and we want image on the right! */}
                {props.mediaMode === 'hidden' ? null : imageRight && renderImage}
            </StyledLandscapeLink>
        </StyledLandscape>
    )
}
Landscape.displayName = 'Landscape'
